// src/components/QuoteBuilder.js (FINAL, COMPLETE, CORRECTED)

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Save, Mail, RefreshCw } from 'lucide-react';
import QuoteSummaryDetail from './QuoteSummaryDetail';
import CostBreakdownChart from './CostBreakDownChart';
import SavedQuotesPanel from './SavedQuotesPanel';
import { useSavedQuotes } from './hooks/useSavedQuotes';
import { FileText } from 'lucide-react';
import {
  calculateInfrastructureCosts,
  calculateSupportCosts,
  calculateSummary,
  CONTRACT_DISCOUNTS,
  serviceData,
  fetchServicePrices
} from '../utils/quoteUtils.js';
import CustomerDetailsStep from './steps/CustomerDetailsStep';
import RegionSelectionStep from './steps/RegionSelectionStep';
import WorkloadSelectionStep from './steps/WorkloadSelectionStep';
import AdditionalEnvStep from './steps/AdditionalEnvStep';
import NetworkConfigStep from './steps/NetworkConfigStep';
import AccessReqsStep from './steps/AccessReqsStep';
import SupportTierStep from './steps/SupportTierStep';
import ContractLengthStep from './steps/ContractLengthStep';
import InternetAccessStep from './steps/InternetAccessStep.js';
import { sendEmailWithAttachment } from '../services/emailApi';
import { generateSOW } from './SOWGenerator'; // Corrected import


function QuoteBuilder() {
  const [state, setState] = useState({
    customerName: '',
    customerAddress: '',
    contactFirstName: '',
    contactLastName: '',
    contactTitle: '',
    emailAddress: '',
    phoneNumber: '',
    streetNumber: '',
    streetName: '',
    city: '',
    state: '',
    postcode: '',
    country: '',
    region: '',
    workload: '',
    uatWorkload: '',
    drWorkload: '',
    uatServer: false,
    drServer: false,
    siteCount: 0,
    centralizedVpn: false,
    privateNlb: false,
    publicNlb: false,
    privateAlb: false,
    publicAlb: false,
    requiresInternet: true,
    natGateways: 1,
    s3Storage: 2,
    instanceStorage: 250,
    supportTier: 'standard',
    contractLength: 12,
    operatingSystem: 'RHEL'
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [showSavedQuotes, setShowSavedQuotes] = useState(false);
  const quoteSummaryRef = useRef();
  const { savedQuotes, saveQuote, deleteQuote } = useSavedQuotes();
  const [exchangeRate, setExchangeRate] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState('');
  const [priceState, setPriceState] = useState({
    prices: null,
    isLoading: false,
    error: null,
    lastUpdated: null
  });

    // Exchange rate fetch
  useEffect(() => {
    const fetchExchangeRate = async () => {
      try {
        const response = await fetch('https://open.er-api.com/v6/latest/USD');
        const data = await response.json();
        if (data.result === "success" && data.rates && data.rates.AUD) {
          setExchangeRate(data.rates.AUD);
        }
      } catch (error) {
        console.error('Error fetching exchange rate:', error);
      }
    };
    fetchExchangeRate();
  }, []);

  const fetchPrices = useCallback(async () => {
    if (!state.region || !state.workload) return;

    setPriceState(prev => ({ ...prev, isLoading: true }));
    try {
      const fetchedPrices = await fetchServicePrices(state);
      if (Object.values(fetchedPrices).some(price => price !== null)) {
        setPriceState({
          prices: fetchedPrices,
          isLoading: false,
          error: null,
          lastUpdated: new Date()
        });
      } else {
        throw new Error('Failed to fetch valid prices');
      }
    } catch (err) {
      setPriceState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Unable to fetch current prices. Using default values.',
        lastUpdated: null
      }));
      console.error(err);
    }
  }, [state]);

    useEffect(() => {
    fetchPrices();
  }, [
    state.region,
    state.workload,
    state.privateAlb,
    state.publicAlb,
    state.privateNlb,
    state.publicNlb,
    state.natGateways,
    state.s3Storage,
    state.instanceStorage,
    state.siteCount,
    fetchPrices,
  ]);
  const calculateQuote = () => {
    if (!state.region || !state.workload || !priceState.prices) return null;

    const infrastructure = calculateInfrastructureCosts(state, exchangeRate, serviceData, priceState.prices);
    const support = calculateSupportCosts(infrastructure.total, state, serviceData);
    const summary = calculateSummary(infrastructure.total, support); // No implementation fee

    return { infrastructure, support, summary };
  };

  const handleSaveQuote = () => {
    const quote = calculateQuote();
    if (!quote) return;

    const quoteData = {
      summary: quote.summary,
      infrastructure: quote.infrastructure,
      support: quote.support,
      state: {
        region: state.region,
        workload: state.workload,
        uatServer: state.uatServer,
        drServer: state.drServer,
        siteCount: state.siteCount,
        supportTier: state.supportTier,
        contractLength: state.contractLength,
          uatWorkload: state.uatWorkload,
          drWorkload: state.drWorkload
      }
    };

    saveQuote(quoteData);
  };

  const generateQuoteHTML = (quote, state, serviceData) => {
    if (!quote || !quote.infrastructure || !quote.support || !quote.summary) {
      console.error('Invalid quote object:', quote);
      return 'Error: Unable to generate quote summary due to missing data.';
    }

    const formatCost = (cost) => {
      if (typeof cost === 'number') {
        return `$${cost.toFixed(2)}`;
      } else if (typeof cost === 'string' && !isNaN(parseFloat(cost))) {
        return `$${parseFloat(cost).toFixed(2)}`;
      } else {
        console.warn('Invalid cost value:', cost);
        return 'N/A';
      }
    };

    const additionalEnvironments = ['Production', state.uatServer && 'UAT', state.drServer && 'DR'].filter(Boolean).join(', ');
    const isPaperless = state.emailAddress.toLowerCase().endsWith('paperlesswms.com.au');
    const supportTier = serviceData.supportTiers[state.supportTier];
    const supportTierName = isPaperless ? `${supportTier.name.split('(')[0].trim()} (24x7)` : supportTier.name;
    const supportHours = isPaperless ? '24x7' : supportTier.description.match(/$$(.*?)$$/)?.[1] || 'N/A';



    const costRows = [
      ...Object.entries(quote.infrastructure.costs.environment),
      ...Object.entries(quote.infrastructure.costs.network),
      ...Object.entries(quote.infrastructure.costs.storage)
    ]
      .filter(([_, item]) => item.cost > 0)
      .map(([_, item]) => `<tr><td>${item.description}</td><td>${formatCost(item.cost)}</td></tr>`)
      .join('');

      return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>AWS MSP Quote Summary - Prepared for ${state.customerName}</title>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
          .container { max-width: 800px; margin: 0 auto; padding: 20px; }
          h1, h2 { color: #0066cc; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
          th { background-color: #f2f2f2; }
        </style>
      </head>
      <body>
        <div class="container">
          <h1>Quote Summary - Prepared for ${state.customerName}</h1>
          <p>Generated on: ${new Date().toLocaleString()}</p>
          
          <h2>Configuration Details</h2>
          <table>
            <tr><th>Region</th><td>${serviceData.regions[state.region]?.name || 'N/A'}</td></tr>
            <tr><th>Workload Type</th><td>${serviceData.instanceTypes[state.workload]?.name || 'N/A'}</td></tr>
            <tr><th>Additional Environments</th><td>${additionalEnvironments}</td></tr>
            <tr><th>Connected Sites</th><td>${state.siteCount || 'N/A'}</td></tr>
            <tr><th>Support Level</th><td>${supportTierName}</td></tr>
            <tr><th>Contract Length</th><td>${state.contractLength || 'N/A'} months</td></tr>
            <tr><th>Instance Storage</th><td>${state.instanceStorage}GB</td></tr>
            <tr><th>S3 Storage</th><td>${state.s3Storage}GB</td></tr>
          </table>
  
          <h2>Cost Breakdown</h2>
          <table>
            <tr><th>Category</th><th>Cost (AUD)</th></tr>
            ${costRows}
            <tr><td>Support & Management (${quote.support.tierDetails.name})</td><td>${formatCost(quote.support.total)}</td></tr>
            ${quote.summary.discountAmount > 0 ?
              `<tr><td>Contract Discount (${quote.summary.discountPercentage}%)</td><td>-${formatCost(quote.summary.discountAmount)}</td></tr>` : ''
            }
            <tr><th>Total Monthly Cost</th><th>${formatCost(quote.summary.finalTotal)}</th></tr>
          </table>
  
          <h2>Support Details</h2>
          <table>
            <tr><th>Support Tier</th><td>${supportTierName}</td></tr>
            <tr><th>Support Hours</th><td>${supportHours}</td></tr>
          </table>
  
          <h2>Contract Details</h2>
          <table>
            <tr><th>Contract Length</th><td>${state.contractLength} months</td></tr>
            <tr><th>Total Contract Value</th><td>${formatCost(parseFloat(quote.summary.finalTotal) * state.contractLength)}</td></tr>
          </table>

          <h2>Implementation Fee (Not Included)</h2>
          <p>
              This quote covers the ongoing monthly costs for AWS infrastructure hosting and our management services.  It does <strong>not</strong> include a one-time implementation fee for the initial setup and configuration of your environment.
          </p>
          <p>
              Implementation costs can vary depending on the complexity of your project, including factors such as:
          </p>
          <ul>
              <li>Number of environments (Production, UAT, DR)</li>
              <li>Network configuration (number of sites, VPN setup)</li>
              <li>Data migration requirements</li>
              <li>Integration with existing systems</li>
              <li>Specific security and compliance needs</li>
          </ul>
            <p>
              Ballpark estimates for implementation fees (excluding GST):</p>
          <ul>
              <li><strong>Basic Setup (Single environment, minimal configuration):</strong>  $2,000 - $5,000</li>
              <li><strong>Standard Setup (Production + UAT, basic networking):</strong> $5,000 - $10,000</li>
              <li><strong>Complex Setup (Production + UAT + DR, multiple sites, complex networking):</strong> $10,000+</li>
          </ul>
          <p>
              Please contact our team to discuss your specific requirements and receive a detailed implementation cost estimate.  We will finalize the project scope and implementation fee after a thorough assessment.
          </p>
  
          <p>For more details or to discuss this quote, please contact our sales team.</p>
        </div>
      </body>
      </html>
    `;
  };

    const handleGenerateSOW = async () => {
    const quote = calculateQuote();
    if (!quote) return;

    try {
      setIsGenerating(true);
      setGenerationProgress('Generating Word document...');

      const wordBlob = await generateSOW(quote, state, serviceData.regions, serviceData.instanceTypes, serviceData.supportTiers);

      // Email the DOCX
      setGenerationProgress('Emailing document...');
      const fileName = `AWS_MSP_SOW_${state.customerName.replace(/\s+/g, '_')}.docx`;
      const file = new File([wordBlob], fileName, {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });

      await sendEmailWithAttachment(
        'j.greenfield@uniware.com.au', // Replace with your email for testing
        'New SOW Generated for Review',
        'Please find attached the newly generated SOW for review.',
        file
      );
      toast.success('SOW emailed successfully for review.');

    } catch (error) {
      console.error('Error in SOW generation process:', error);
      toast.error('Error generating or emailing SOW. Please try again.');
    } finally {
      setIsGenerating(false);
      setGenerationProgress('');
    }
  };

    const handleLoadQuote = (savedQuote) => {
        setState(savedQuote.state);
        setPriceState(prev => ({
            ...prev,
            prices: savedQuote.priceSnapshot // Restore the prices from when quote was saved
        }));
        setShowSavedQuotes(false);
    };

  const handleEmailQuote = async () => {
    const quote = calculateQuote();
    if (!quote) {
      console.error('Failed to calculate quote:', quote);
      toast.error('Unable to generate quote. Please check your inputs.');
      return;
    }

    console.log('Generated quote:', JSON.stringify(quote, null, 2));

    const htmlContent = generateQuoteHTML(quote, state, serviceData);

    try {
      if (!state.emailAddress) {
        throw new Error('Customer email address is missing');
      }

      await sendEmailWithAttachment(
        state.emailAddress,
        'AWS MSP Quote Summary',
        'Please find attached your AWS MSP quote summary.',
        htmlContent,
        true
      );

      toast.success('Quote sent to your email successfully!');
    } catch (error) {
      console.error('Error sending quote email:', error);
      toast.error(`Error: ${error.message}. Please try again or contact support.`);
    }
  };
    const renderQuoteSummary = () => {
    if (priceState.isLoading) {
      return (
        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <RefreshCw className="animate-spin h-8 w-8 text-blue-500 mx-auto mb-4" />
            <p className="text-gray-600">Fetching latest AWS pricing...</p>
          </div>
        </div>
      );
    }

    if (priceState.error) {
      return (
        <div className="p-6 bg-amber-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-800 font-medium mb-2">Using default prices. Live pricing unavailable.</p>
              <p className="text-amber-600 text-sm">{priceState.error}</p>
            </div>
            <button
              onClick={fetchPrices}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Retry
            </button>
          </div>
        </div>
      );
    }

    const quote = calculateQuote();
    if (!quote) return null;

    return (
      <div className="space-y-6" ref={quoteSummaryRef}>
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-2xl font-semibold text-gray-800">Quote Summary</h2>
            {priceState.lastUpdated && (
              <p className="text-sm text-gray-500">
                Prices updated: {priceState.lastUpdated.toLocaleString()}
                <button
                  onClick={fetchPrices}
                  className="ml-2 text-blue-500 hover:text-blue-600"
                  title="Refresh prices"
                >
                  <RefreshCw className="h-4 w-4 inline" />
                </button>
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowSavedQuotes(!showSavedQuotes)}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
            >
              <Save size={16} />
              Saved Quotes
            </button>
            <button
              onClick={handleSaveQuote}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <Save size={16} />
              Save Quote
            </button>
            <button
              onClick={handleEmailQuote}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Mail size={16} />
              Email Quote
            </button>
             <button
                onClick={handleGenerateSOW}
                disabled={isGenerating}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                >
                <FileText size={16} />
                {isGenerating ? 'Generating...' : 'Request SOW'}
                </button>
                {isGenerating && (
                <div className="loading-overlay">
                    <div className="loading-content">
                    <div className="spinner"></div>
                    <p>{generationProgress}</p>
                    </div>
                </div>
                )}
          </div>
        </div>

        {showSavedQuotes && (
          <SavedQuotesPanel
            savedQuotes={savedQuotes}
            onDelete={deleteQuote}
            onLoad={handleLoadQuote}
          />
        )}

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Configuration Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
            <p><span className="font-medium">Region:</span> {serviceData.regions[state.region].name}</p>
            <p><span className="font-medium">Workload Type:</span> {serviceData.instanceTypes[state.workload].name}</p>
            <p>
              <span className="font-medium">Additional Environments:</span>{' '}
              {[
                'Production',
                state.uatServer && 'UAT',
                state.drServer && 'DR'
              ].filter(Boolean).join(', ')}
            </p>
            <p><span className="font-medium">Connected Sites:</span> {state.siteCount}</p>
            <p><span className="font-medium">Support Level:</span> {serviceData.supportTiers[state.supportTier].name}</p>
            <p><span className="font-medium">Contract Length:</span> {state.contractLength} months</p>
          </div>
        </div>
        <CostBreakdownChart
          envCosts={{
            production: quote.infrastructure.costs.environment.production.cost || 0,
            uat: quote.infrastructure.costs.environment.uat.cost || 0,
            dr: quote.infrastructure.costs.environment.dr.cost || 0
          }}
          networkCosts={{
            vpn: quote.infrastructure.costs.network.vpn.cost || 0,
            privateNlb: quote.infrastructure.costs.network.privateNlb.cost || 0,
            publicNlb: quote.infrastructure.costs.network.publicNlb.cost || 0,
            privateAlb: quote.infrastructure.costs.network.privateAlb.cost || 0,
            publicAlb: quote.infrastructure.costs.network.publicAlb.cost || 0,
          }}
          storageCosts={{
            s3: quote.infrastructure.costs.storage.s3Storage.cost || 0,
            instance: quote.infrastructure.costs.storage.instanceStorage.cost || 0
          }}
          managedServices={parseFloat(quote.support.total) || 0}
        />

        <QuoteSummaryDetail
          quote={quote}
          state={state}
          regionPricing={serviceData.regions}
          instanceTypes={serviceData.instanceTypes}
          supportTiers={serviceData.supportTiers}
          priceLastUpdated={priceState.lastUpdated}
        />
          {/* Implementation Fee Information */}
        <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Implementation Fee (Not Included)</h3>
            <p className="text-sm text-gray-600 mb-4">
                This quote covers the ongoing monthly costs for AWS infrastructure hosting and our management services.  It does <strong>not</strong> include a one-time implementation fee for the initial setup and configuration of your environment.
            </p>
            <p className="text-sm text-gray-600 mb-2">
                Implementation costs can vary depending on the complexity of your project, including factors such as:
            </p>
            <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 mb-4">
                <li>Number of environments (Production, UAT, DR)</li>
                <li>Network configuration (number of sites, VPN setup)</li>
                <li>Data migration requirements</li>
                <li>Integration with existing systems</li>
                <li>Specific security and compliance needs</li>
            </ul>
             <p className="text-sm text-gray-600 mb-2">
                Ballpark estimates for implementation fees (excluding GST):</p>
            <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 mb-4">
                <li><strong>Basic Setup (Single environment, minimal configuration):</strong>  $2,000 - $5,000</li>
                <li><strong>Standard Setup (Production + UAT, basic networking):</strong> $5,000 - $10,000</li>
                <li><strong>Complex Setup (Production + UAT + DR, multiple sites, complex networking):</strong> $10,000+</li>
            </ul>
            <p className="text-sm text-gray-600">
                Please contact our team to discuss your specific requirements and receive a detailed implementation cost estimate.  We will finalise the project scope and implementation fee after a thorough assessment.
            </p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Cost Optimisation Recommendations</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            {state.contractLength < 36 && (
              <li className="flex items-start gap-2">
                <span className="text-blue-500">•</span>
                Consider a 36-month contract for a 20% discount instead of the current {state.contractLength}-month contract ({CONTRACT_DISCOUNTS[state.contractLength] * 100}% discount)
              </li>
            )}
            {!state.centralizedVpn && state.siteCount > 2 && (
              <li className="flex items-start gap-2">
                <span className="text-blue-500">•</span>
                Implementing centralized VPN could reduce network costs for your {state.siteCount} sites
              </li>
            )}
            {state.supportTier === 'enterprise' && !state.drServer && (
              <li className="flex items-start gap-2">
                <span className="text-blue-500">•</span>
                Consider adding DR environment to match your enterprise support level requirements
              </li>
            )}
          </ul>
        </div>
      </div>
    );
  };

  const renderProgressBar = () => {
    const steps = 10; // Back to 10 steps
    return (
      <div className="max-w-5xl mx-auto mb-8">
        <div className="flex items-center justify-center space-x-2 mb-2">
          {[...Array(steps).keys()].map(i => {
            const stepNumber = i + 1;
            const isActive = currentStep === stepNumber;
            const isComplete = currentStep > stepNumber;
            return (
              <div key={stepNumber} className="flex items-center">
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center
                  ${isActive ? 'bg-blue-600 text-white'
                    : isComplete ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-800'}
                `}>
                  {isComplete ? '✓' : stepNumber}
                </div>
                {stepNumber < steps && (
                  <div className={`h-1 w-12 mx-1 ${currentStep > stepNumber ? 'bg-green-500' : 'bg-gray-200'}`} />
                )}
              </div>
            );
          })}
        </div>
        <div className="text-sm text-gray-600 text-center">
          Step {currentStep} of {steps}
        </div>
      </div>
    );
  };

  const renderStep = () => {
    const commonProps = {
      state,
      setState,
      serviceData,
      setCurrentStep,
      exchangeRate,
      priceState
    };

    switch (currentStep) {
      case 1:
        return <CustomerDetailsStep {...commonProps} />;
      case 2:
        return <RegionSelectionStep {...commonProps} />;
      case 3:
        return <WorkloadSelectionStep {...commonProps} />;
      case 4:
        return <AdditionalEnvStep {...commonProps} />;
      case 5:
        return <NetworkConfigStep {...commonProps} />;
      case 6:
        return <AccessReqsStep {...commonProps} />;
      case 7:
        return <InternetAccessStep {...commonProps} />;
      case 8:
        return <SupportTierStep {...commonProps} />;
      case 9:
        return <ContractLengthStep {...commonProps} />;
      case 10:
        return renderQuoteSummary();
      default:
        return null;
    }
  };
    return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-10 px-4">
      {renderProgressBar()}

      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6">
            <h1 className="text-3xl font-bold text-white">AWS MSP Quote Builder</h1>
            <p className="text-blue-100 mt-2">Build your custom AWS managed services quote</p>
          </div>

          <div className="p-8">
            {renderStep()}

            {currentStep < 10 && (
              <div className="mt-8 flex justify-between items-center">
                {currentStep > 1 && (
                  <button
                    onClick={() => setCurrentStep(currentStep - 1)}
                    className="text-gray-600 hover:text-gray-800 font-medium"
                  >
                    ← Back
                  </button>
                )}
                <button
                  onClick={() => setCurrentStep(Math.min(10, currentStep + 1))}
                  className="ml-auto px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                >
                   'Continue →'
                </button>
              </div>
            )}

             {currentStep === 10 && (
              <div className="mt-8">
                <button
                  onClick={() => {
                    setState({
                      customerName: '',
                      customerAddress: '',
                      contactFirstName: '',
                      contactLastName: '',
                      contactTitle: '',
                      emailAddress: '',
                      phoneNumber: '',
                      region: '',
                      workload: '',
                      uatWorkload: '',
                      drWorkload: '',
                      uatServer: false,
                      drServer: false,
                      siteCount: 0,
                      centralizedVpn: false,
                      privateNlb: false,
                      publicNlb: false,
                      privateAlb: false,
                      publicAlb: false,
                      requiresInternet: true,
                      natGateways: 1,
                      s3Storage: 2,
                      instanceStorage: 250,
                      supportTier: 'standard',
                      contractLength: 12,
                      operatingSystem: 'RHEL'
                    });
                    setPriceState({
                      prices: null,
                      isLoading: false,
                      error: null,
                      lastUpdated: null
                    });
                    setCurrentStep(1);
                  }}
                  className="w-full px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-700 font-medium"
                >
                  Start New Quote
                </button>
              </div>
            )}
          </div>
          {/* Price Update Status */}
          {(priceState.isLoading || priceState.error) && (
            <div className={`fixed bottom-4 right-4 p-4 rounded-lg shadow-lg ${priceState.error ? 'bg-red-50' : 'bg-blue-50'
              }`}>
              {priceState.isLoading && (
                <div className="flex items-center text-blue-700">
                  <RefreshCw className="animate-spin h-4 w-4 mr-2" />
                  Updating prices...
                </div>
              )}
              {priceState.error && (
                <div className="flex items-center text-red-700">
                  <span className="mr-2">⚠️</span>
                  {priceState.error}
                  <button
                    onClick={fetchPrices}
                    className="ml-2 text-red-700 hover:text-red-800 underline"
                  >
                    Retry
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default QuoteBuilder;
